import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, ScrollView, StyleSheet } from 'react-native';
import qs from 'qs'
import { compose } from 'redux';
import debounce from 'lodash/debounce';
import { DNABox, DNACard, DNADivider, DNASpinner, DNAText, Iffy, util } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import useFixedHeader from '@alucio/lux-ui/src/hooks/useFixedLayout'
import { DNAEmpty, DocumentSearchResultRow, EmptyVariant } from 'src/components/DNA'
import { useSort, withSort } from 'src/components/DNA/hooks/useSort'
import { filters } from 'src/state/redux/document/query'
import {
  DNADocumentFiltersChips,
  useDNADocumentFilters,
  withDNADocumentFilters,
} from 'src/components/DNA/Document/DNADocumentFilters'
import { useCanPerformSearch } from 'src/state/redux/selector/documentSearch/documentSearch'
import { useHistory, useLocation } from 'src/router'
import FiltersSidebar from 'src/components/QuickFilters/FiltersSidebar'
import FilterButton, { VariantOptions } from 'src/components/QuickFilters/FilterButton'
import QuickFiltersProvider from 'src/components/QuickFilters/QuickFiltersProvider'
import { useDocumentSearchV2 } from 'src/hooks/useDocumentSearchV2'
import { DocumentORM } from 'src/types/orms'
import VersioningPanelWrapper from '../Publishers/Versioning/VersioningPanel/VersioningPanel'
import ListSkeleton from 'src/components/ListSkeleton/ListSkeleton'
import useIsDebugView from 'src/hooks/useIsDebugView/useIsDebugView'
import { SearchAdapterSelector, SearchScoreThresholdInput } from '../Debug/Tabs/Search'
import { ActionCallbacks } from 'src/components/DNA/Document/DNADocument.actions'

const styles = StyleSheet.create({
  mainWrapper: {
    backgroundColor: colors['color-text-white'],
    flex: 1,
    flexDirection: 'column',
    minHeight: 'calc(100vh - 160px)',
    minWidth: 'auto',
    paddingHorizontal: 33,
    paddingVertical: 24,
  },
  content: {
    borderWidth: 1,
    borderColor: colors['color-gray-100'],
  },
  filtersSidebarScrollView: {
    maxHeight: '100vh',
    position: 'sticky' as any,
    top: 0,
    zIndex: 10,
    paddingBottom: 350,
  },
  filersSidebarNotVisible: {
    display: 'none',
  },
});

interface SearchDebugInfoProps {
  searchTiming: {
    serverTime: number | null | undefined;
    clientTime: number | null | undefined;
    totalDuration: number | null | undefined;
    processedQuery: string | null | undefined;
    errorMessage: string | null | undefined;
  };
  onChange: (value: string) => void;
}

const SearchDebugInfo: React.FC<SearchDebugInfoProps> = ({ searchTiming, onChange }) => {
  const isDebugView = useIsDebugView();

  if (!isDebugView) return null;

  // Group timing metrics for better organization
  const timingMetrics = [
    { label: 'Server Time', value: searchTiming.serverTime },
    { label: 'Client Time', value: searchTiming.clientTime },
    { label: 'Total Duration', value: searchTiming.totalDuration },
  ];

  return (
    <DNABox appearance="col" spacing="md">
      <DNAText h5>Debug Info</DNAText>
      <DNACard appearance="outline">
        <DNABox appearance="col" spacing="md" style={{ padding: 16 }}>
          {/* Search Adapter Section */}
          <DNABox appearance="col" spacing="sm">
            <DNAText h5>Search Adapter</DNAText>
            <SearchAdapterSelector onChange={onChange} />
          </DNABox>

          <SearchScoreThresholdInput onChange={onChange} />

          {/* Timing Metrics Section */}
          <DNABox appearance="col" spacing="sm">
            <DNAText h5>Timing Metrics</DNAText>
            {timingMetrics.map(({ label, value }) => (
              <DNABox key={label} appearance="row" spacing="sm">
                <DNAText>{label}:</DNAText>
                <DNAText>{value ?? 'N/A'}</DNAText>
              </DNABox>
            ))}
          </DNABox>

          {/* Query Information Section */}
          <DNABox appearance="col" spacing="sm">
            <DNAText h5>Query Information</DNAText>
            <DNABox appearance="col" spacing="xs">
              <DNAText>Processed Query:</DNAText>
              <DNACard appearance="flat">
                <DNAText italic style={{ flexWrap: 'wrap' }}>
                  {searchTiming.processedQuery ?? 'N/A'}
                </DNAText>
              </DNACard>
            </DNABox>
          </DNABox>

          {/* Error Section - Only show if there's an error */}
          {searchTiming.errorMessage && (
            <DNABox appearance="col" spacing="sm">
              <DNAText h5>Error</DNAText>
              <DNACard appearance="flat">
                <DNAText style={{ padding: 8, color: colors['color-error'] }}>
                  {searchTiming.errorMessage}
                </DNAText>
              </DNACard>
            </DNABox>
          )}
        </DNABox>
      </DNACard>
    </DNABox>
  );
};

const PublisherSearchResults: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const qsParam = qs.parse(location.search, { ignoreQueryPrefix: true })
  const [searchText, setSearchText] = useState(qsParam.searchText as string ?? '');
  const searchTextHolder = useRef(qsParam.searchText as string ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const { sortSelectorOpts } = useSort()
  const fixedHeaderWidth = useFixedHeader();
  const { filterSelectorOpts } = useDNADocumentFilters()
  const [filtersVisible, setFiltersVisible] = useState(false);
  const canPerformSearch = useCanPerformSearch();
  const [documentId, setDocumentId] = useState<string>();

  useEffect(() => {
    const searchInputReference = document.getElementById('header-search-input') as HTMLInputElement;
    const debouncedKeyDownHandler = debounce(() => {
      setSearchText(searchTextHolder.current)
      setIsLoading(false)

      // Update the url with  the new search text
      history.replace({
        pathname: location.pathname,
        search: qs.stringify({ ...qsParam, searchText: searchTextHolder.current }),
      })
    }, 500);

    const keyDownHandler = (newSearchText: string) => {
      const canSearch = canPerformSearch(newSearchText.trim());
      if (canSearch && !isLoading) {
        setIsLoading(true)
      }
      // Store the updated text in a ref to update
      // the state searchText in the debounced handler
      searchTextHolder.current = newSearchText
      debouncedKeyDownHandler()
    }

    const keyDownHandlerWrapper = () => {
      if (searchInputReference.value !== searchTextHolder.current) {
        keyDownHandler(searchInputReference.value)
      }
    }

    const clearClickHandler = (e: any) => {
      if (e.target?.closest("[data-testid='header-search-input-clear']")) {
        keyDownHandler('')
      }
    }
    searchInputReference?.addEventListener('keyup', keyDownHandlerWrapper);

    // We bind directly to document for clear search icon click because the icon
    // is being re-rendered by Search Input and ref would be lost
    document.addEventListener('click', clearClickHandler)
    return () => {
      searchInputReference?.removeEventListener('keyup', keyDownHandlerWrapper);
      document.removeEventListener('click', clearClickHandler)
    }
  }, []);

  const {
    documentORMSearchResults: searchedDocs,
    isLoadingSearch,
    status,
    searchTiming,
    fullTextSearch,
    onPreviewDocumentFromSearchResult,
  } = useDocumentSearchV2(
    searchText,
    'PUBLISHER_RESULTS',
    filters.nonDeleted,
    filterSelectorOpts,
    sortSelectorOpts,
  )

  const pluralResults = searchedDocs.length > 1 ? 's' : ''

  const title = isLoadingSearch
    ? 'Loading results...'
    : `Search results for "${searchText}" | ${searchedDocs.length} result${pluralResults}`

  const onSelectResult = (documentORM: DocumentORM): void => {
    const documentVersionId = documentORM.relations.version.latestDocumentVersionORM.model.id;
    onPreviewDocumentFromSearchResult(searchedDocs, documentVersionId);
    setDocumentId(documentORM.model.id);
  };

  const actionCallbacks: ActionCallbacks = useMemo(
    () => ({
      version: (documentORM) => { setDocumentId(documentORM.model.id) },
    }),
    [],
  )

  const renderSearchItem = ({ item: documentORM }: { item: DocumentORM }) => {
    return (
      <DocumentSearchResultRow
        documentORM={documentORM}
        onPress={() => onSelectResult(documentORM)}
        actionCallbacks={actionCallbacks}
      />
    )
  }

  const onChange = (_value: string) => {
    const currentSearchText = (searchText || qsParam.searchText as string) ?? '';
    fullTextSearch(currentSearchText, 'PUBLISHER_RESULTS')
  }

  return (
    <QuickFiltersProvider quickFilters={[]}>
      <DNABox style={styles.mainWrapper} spacing="md">
        <DNABox fill spacing="md" appearance="col">
          {/* Header */}
          <DNABox alignY="end" spacing="between" style={{ maxWidth: fixedHeaderWidth }}>
            <DNABox appearance="row" alignY="center" spacing="sm">
              <Iffy is={isLoadingSearch}>
                <DNASpinner size="small" />
              </Iffy>
              <DNAText testID="page-title" h5>{title}</DNAText>
            </DNABox>
          </DNABox>

          <Iffy is={!isLoadingSearch}>
            <DNADivider />
          </Iffy>

          {<SearchDebugInfo searchTiming={searchTiming} onChange={onChange} />}

          {/* Content */}
          <DNABox appearance="col" spacing="md">
            <Iffy is={!isLoadingSearch}>
              <DNABox testID="filters-header-button" spacing="sm">
                <FilterButton
                  setFiltersVisible={setFiltersVisible}
                  filtersVisible={filtersVisible}
                  variant={VariantOptions.publisher}
                />
                <DNADocumentFiltersChips documents={searchedDocs} isLoading={isLoadingSearch}/>
              </DNABox>
            </Iffy>
            <DNABox appearance="row">
              <DNABox fill>
                <DNABox>
                  <ScrollView
                    style={util.mergeStyles(
                      undefined,
                      styles.filtersSidebarScrollView,
                      [styles.filersSidebarNotVisible, !filtersVisible],
                    )}
                  >
                    <FiltersSidebar setVisible={setFiltersVisible} textValue={searchText} />
                  </ScrollView>
                </DNABox>
                <Iffy is={isLoadingSearch}>
                  <DNACard appearance="outline">
                    <ListSkeleton numberOfItems={5} />
                  </DNACard>
                </Iffy>
                <Iffy is={status === 'RESULTS' && !!searchedDocs.length}>
                  <DNACard
                    appearance="outline"
                    style={[
                      filtersVisible && { paddingLeft: 16 },
                      { width: fixedHeaderWidth - (filtersVisible ? 400 : 0) },
                    ]}
                  >
                    <FlatList
                      data={searchedDocs}
                      renderItem={renderSearchItem}
                      ItemSeparatorComponent={DNADivider}
                      keyExtractor={(item) => item.model.id}
                    />
                  </DNACard>
                </Iffy>
                <Iffy is={status === 'EMPTY_RESULTS' || (!searchedDocs.length && (status !== 'PENDING'))}>
                  <DNAEmpty emptyVariant={EmptyVariant.DocumentListEmptySearch} />
                </Iffy>
              </DNABox>
            </DNABox>
          </DNABox>
        </DNABox>
        <VersioningPanelWrapper documentId={documentId} setDocumentId={setDocumentId} />
      </DNABox>
    </QuickFiltersProvider>
  );
}

export default compose(
  withDNADocumentFilters,
  withSort,
)(PublisherSearchResults)
